import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { appendUtm } from '../utils/utm';

/** Site-wide "sign-in required" lock. When the visitor is anonymous
 *  (regardless of grace) AND they're on one of the gated sections, we:
 *    1. Apply a global blur to <main> content via a body class
 *    2. Overlay a signup card centered on the viewport
 *  Logged-in readers see nothing. Grace no longer bypasses the blur - the
 *  visual is a permanent conversion nudge for anon visitors on paid sections.
 */
const LOCKED_PATHS = ['/explicit', '/the-rumour-file', '/ganglandmap', '/experiences'];

const SECTION_LABELS = {
  '/explicit': { title: '18+ Videos', why: 'Videos are subscribers-only. Sign up in one tap.' },
  '/the-rumour-file': { title: 'The Rumour File', why: 'The Rumour File is subscribers-only.' },
  '/ganglandmap': { title: 'Gangland Map', why: 'The live Gangland Map is subscribers-only.' },
  '/experiences': { title: 'The Madame Files', why: 'The Madame Files is subscribers-only.' },
};

const SectionLockOverlay = () => {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();
  const isLocked = LOCKED_PATHS.includes(pathname);

  React.useEffect(() => {
    const show = !loading && isLocked && !user;
    document.body.classList.toggle('outlaw-locked-blur', show);
    return () => document.body.classList.remove('outlaw-locked-blur');
  }, [loading, isLocked, user]);

  if (loading || !isLocked || user) return null;

  const meta = SECTION_LABELS[pathname] || { title: 'Reader account required', why: '' };

  return (
    <>
      <style>{`
        /* Blur everything except the fixed top nav + this overlay while the
           anon lock is active. */
        body.outlaw-locked-blur main,
        body.outlaw-locked-blur section:not([data-lock-overlay]),
        body.outlaw-locked-blur footer,
        body.outlaw-locked-blur div[data-testid$='-page'] > div:not([data-lock-overlay]) {
          filter: blur(10px);
          pointer-events: none !important;
          user-select: none !important;
        }
      `}</style>
      <div
        data-lock-overlay
        data-testid="section-lock-overlay"
        className="fixed inset-0 z-[45] flex items-start justify-center pt-32 sm:pt-40 px-4 pointer-events-none"
      >
        <div className="pointer-events-auto max-w-md w-full text-center rounded-lg border-2 border-red-600/70 bg-gradient-to-b from-red-950/85 to-black shadow-[0_0_60px_rgba(220,38,38,0.4)] p-6 sm:p-8">
          <div className="inline-flex items-center gap-2 text-red-400 text-[10px] font-black uppercase tracking-[0.3em] mb-3">
            <Lock className="h-3.5 w-3.5" /> {meta.title}
          </div>
          <h2 className="text-white text-2xl sm:text-3xl font-black leading-tight mb-3" style={{ fontFamily: 'Georgia, serif' }}>
            Reader account required to see this section.
          </h2>
          <p className="text-zinc-300 text-sm sm:text-base mb-3">{meta.why}</p>
          <p className="text-yellow-300/90 text-xs mb-5">
            Reports are for subscribers only. Please choose your unlimited subscription plan, from $13/mo.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to={appendUtm("/subscribe")}
              data-testid="section-lock-signup"
              className="inline-flex items-center justify-center px-5 py-3 rounded bg-red-600 hover:bg-red-500 text-white font-black text-xs uppercase tracking-[0.25em]"
            >
              Choose your plan
            </Link>
            <Link
              to={appendUtm("/login")}
              data-testid="section-lock-login"
              className="inline-flex items-center justify-center px-5 py-3 rounded bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-white font-bold text-xs uppercase tracking-[0.25em]"
            >
              Log in
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default SectionLockOverlay;
